import React, { useState, useCallback, useRef } from "react";
import { PlxsText, PlxsButton, PlxsAlert, PlxsIcon } from "@plexlogic/plexus-ui";
interface UploadDropzoneProps {
  entryMethod: "simple" | "standard" | "advanced";
  onFileUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  hasError: boolean;
  error?: string;
}

const UploadDropzone: React.FC<UploadDropzoneProps> = ({
  onFileUpload,
  isUploading,
  hasError,
  error,
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileInput = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onFileUpload(file);
      }
    },
    [onFileUpload]
  );

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      onFileUpload(files[0]);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--plexus-space-md, 16px)",
      }}
    >
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        style={{
          border: isDragActive
            ? "2px dashed var(--plexus-color-primary, #0066FF)"
            : "1px solid var(--plexus-border-subtle, #f3f4f6)",
          borderRadius: "var(--plexus-radius-lg, 12px)",
          padding: "20px 16px",
          textAlign: "center",
          backgroundColor: isDragActive
            ? "var(--plexus-surface-brand-light, rgba(0, 102, 255, 0.08))"
            : "var(--plexus-surface-subtle, #f9fafb)",
          transition: "all 0.2s ease",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "var(--plexus-space-sm, 12px)",
          minHeight: "120px",
        }}
      >
        {/* Main Text - Centered */}
        <div style={{ textAlign: "center", maxWidth: "400px" }}>
          <PlxsText size="sm">
            {isDragActive
              ? "Drop your file here"
              : "Drag and drop your file, or browse"}
          </PlxsText>
        </div>

        {/* File Input and Button - Centered */}
        <div>
          <input
            ref={fileInputRef}
            type="file"
            id="file-upload"
            accept=".txt,.csv,.tsv"
            onChange={handleFileInput}
            style={{ display: "none" }}
            disabled={isUploading}
          />
          <PlxsButton
            dataTestId="choose-file"
            label="Choose file"
            variant="secondary"
            size="sm"
            onClick={handleButtonClick}
            isLoading={isUploading}
            isDisabled={isUploading}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "var(--plexus-space-sm, 8px)",
        }}
        data-testid="upload-file-hint"
      >
        <PlxsIcon name="infoCircle" size="sm" />
        <PlxsText size="sm">
          Upload CSV files up to 2 GB, with a header row.
        </PlxsText>
      </div>

      {/* Error Message */}
      {hasError && error && <PlxsAlert variant="error">{error}</PlxsAlert>}
    </div>
  );
};

export default UploadDropzone;
